import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grow, useTheme, Fade } from '@mui/material';
import { alpha, keyframes } from '@mui/system';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { Category } from '../../store/slices/catalogSlice';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getCategoryUrl } from '../../utils/categoryCanonical';

interface CatalogMenuProps {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const slideRight = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
`;

const CatalogMenu: React.FC<CatalogMenuProps> = ({
  anchorEl,
  isOpen,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const gold = theme.palette.primary.main;
  const goldLight = theme.palette.primary.light;

  const { categories, loading } = useAppSelector((state) => state.catalog);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (categories.length > 0 && !activeId) {
      setActiveId(categories[0].id);
    }
  }, [categories, activeId]);

  const { containerLeft, containerTop } = useMemo(() => {
    if (!anchorEl) return { containerLeft: 0, containerTop: 0 };
    const rect = anchorEl.getBoundingClientRect();
    const parentRect = anchorEl.offsetParent?.getBoundingClientRect();
    const parentLeft = parentRect ? parentRect.left : 0;
    const parentTop = parentRect ? parentRect.top : 0;
    
    // Align left with the menu button, slightly offset down
    return {
      containerLeft: Math.max(0, rect.left - parentLeft - 10),
      containerTop: rect.bottom - parentTop + 16,
    };
  }, [anchorEl]);

  const activeChildren = useMemo<Category[]>(() => {
    const cat = categories.find((c: Category) => c.id === activeId);
    return cat?.children ?? [];
  }, [categories, activeId]);

  if (!isOpen) return null;

  return (
    <Grow in={isOpen} timeout={300} style={{ transformOrigin: 'top left' }}>
      <Box
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          position: 'absolute',
          top: containerTop,
          left: containerLeft,
          width: 900,
          minHeight: 380,
          display: { xs: 'none', lg: 'flex' },
          zIndex: 1300,
          borderRadius: '24px',
          overflow: 'hidden',
          backgroundColor: isDark ? alpha('#111', 0.85) : alpha('#fff', 0.9),
          backdropFilter: 'blur(20px)',
          boxShadow: isDark 
            ? `0 24px 48px ${alpha('#000', 0.6)}, 0 0 0 1px ${alpha(gold, 0.15)} inset` 
            : `0 24px 48px ${alpha('#000', 0.1)}, 0 0 0 1px ${alpha(gold, 0.2)} inset`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Left Pane: Root Categories */}
        <Box
          sx={{
            width: 280,
            backgroundColor: isDark ? alpha('#000', 0.4) : alpha('#f8f9fa', 0.7),
            borderRight: `1px solid ${isDark ? alpha(gold, 0.1) : alpha(gold, 0.15)}`,
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {loading && categories.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
              Загрузка каталога...
            </Typography>
          )}

          {categories.map((cat: Category) => {
            const isActive = cat.id === activeId;
            return (
              <Box
                key={cat.id}
                component={Link}
                to={getCategoryUrl(cat)}
                onClick={onClose}
                onMouseEnter={() => setActiveId(cat.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.5,
                  borderRadius: '12px',
                  textDecoration: 'none',
                  color: isActive ? (isDark ? goldLight : '#b8860b') : theme.palette.text.primary,
                  backgroundColor: isActive 
                    ? (isDark ? alpha(gold, 0.15) : alpha(gold, 0.08)) 
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isDark ? alpha(gold, 0.2) : alpha(gold, 0.12),
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Typography sx={{ fontWeight: isActive ? 600 : 500, fontSize: '0.95rem' }}>
                  {cat.name}
                </Typography>
                {isActive && (
                  <ChevronRightIcon sx={{ fontSize: '1.2rem', color: isDark ? goldLight : '#b8860b' }} />
                )}
              </Box>
            );
          })}
        </Box>

        {/* Right Pane: Child Categories */}
        <Box sx={{ flex: 1, p: 4, display: 'flex', flexDirection: 'column' }}>
          <Fade in={true} key={activeId} timeout={400}>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 600, 
                  color: isDark ? '#fff' : '#000',
                  borderBottom: `2px solid ${alpha(gold, 0.3)}`,
                  display: 'inline-block',
                  pb: 0.5
                }}
              >
                {categories.find((c: Category) => c.id === activeId)?.name || 'Подкатегории'}
              </Typography>

              <Box 
                sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: 2 
                }}
              >
                {activeChildren.map((child: Category, i: number) => (
                  <Box
                    key={child.id}
                    component={Link}
                    to={getCategoryUrl(child)}
                    onClick={onClose}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      borderRadius: '14px',
                      textDecoration: 'none',
                      color: theme.palette.text.primary,
                      border: `1px solid ${isDark ? alpha('#fff', 0.05) : alpha('#000', 0.05)}`,
                      backgroundColor: isDark ? alpha('#fff', 0.02) : alpha('#fff', 0.5),
                      transition: 'all 0.2s ease',
                      animation: `${slideRight} 0.3s ease ${i * 0.05}s both`,
                      '&:hover': {
                        color: isDark ? goldLight : '#b8860b',
                        borderColor: alpha(gold, 0.3),
                        backgroundColor: isDark ? alpha(gold, 0.05) : alpha(gold, 0.02),
                        boxShadow: `0 4px 12px ${alpha(gold, 0.1)}`,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.95rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {child.name}
                    </Typography>
                  </Box>
                ))}
                
                {activeChildren.length === 0 && !loading && (
                  <Typography variant="body2" sx={{ color: 'text.disabled', gridColumn: '1 / -1' }}>
                    В этой категории пока нет подкатегорий.
                  </Typography>
                )}
              </Box>
            </Box>
          </Fade>
        </Box>
      </Box>
    </Grow>
  );
};

export default CatalogMenu;
